import { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from "@material-ui/core";
import moment from "moment";

import { Card } from "../../components/card/Card";
import { CardContent } from "../../components/card/CardContent";
import { CardHeader } from "../../components/card/CardHeader";
import { Section } from "../../components/section";
import { GreenButton, VerticalButtons, WhiteButton } from "../../components/new-button";
import { Message } from "../../Message";
import { ContentSection } from "../../layout/ContentSection";
import { TitleHeader } from "../../components/TitleHeader";
import { LinearProgress } from "../../components/LinearProgress";
import { NotFoundPage } from "../../NotFoundPage";
import { ErrorPage } from "../../ErrorPage";
import { useTask } from "../../task/tasks.hook";
import { ClarifyClassificationTaskDocument } from "../../task/task.model";
import { useIsSuperAdministrator } from "../../LinkProvider";
import { useCloseTask } from "../../workflow/follow-up/close-task.hook";
import { useSupportEmail } from "../support-email.hook";
import { useSupportEmailContent } from "../support-email-content.hook";

const useStyles = makeStyles({
    section: {
        marginTop: "16px",
        "&:first-child": { marginTop: 0 },
    },
    label: {
        fontWeight: 600,
        marginBottom: "4px",
    },
    body: {
        whiteSpace: "pre-wrap",
        fontFamily: "monospace",
        fontSize: "13px",
        maxHeight: "500px",
        overflowY: "auto",
        background: "#f7f7f7",
        padding: "12px",
        border: "1px solid #ddd",
        borderRadius: "4px",
    },
    htmlFrame: {
        width: "100%",
        height: "500px",
        border: "1px solid #ddd",
        borderRadius: "4px",
        background: "#fff",
    },
    metadata: {
        marginBottom: "12px",
        color: "#666",
        fontSize: "13px",
    },
    attachments: {
        marginTop: "12px",
        color: "#666",
        fontSize: "13px",
    },
});

export const IntegratedClarifyClassificationDetailPage = () => {
    const isSuperAdministrator = useIsSuperAdministrator();

    const { tab, taskId } = useParams<{ tab: string, taskId: string }>();

    const history = useHistory();
    const goBack = useCallback(() => {
        history.push(`/clarify-classification/${tab}`);
    }, [history, tab]);

    const task = useTask(taskId) as ClarifyClassificationTaskDocument | null | undefined;

    if ( !isSuperAdministrator ) return <NotFoundPage />;
    if ( task === null ) return <NotFoundPage />;
    if ( task === undefined ) return <LinearProgress />;

    if ( task.type !== "clarify-classification" ) return <ErrorPage message="Opgavens type genkendes ikke" />;

    return <ClarifyClassificationDetailCard task={task} goBack={goBack} />;
};

interface ClarifyClassificationDetailCardProps {
    task: ClarifyClassificationTaskDocument;
    goBack: () => void;
}

const ClarifyClassificationDetailCard = (props: ClarifyClassificationDetailCardProps) => {
    const classes = useStyles();
    const { task, goBack } = props;

    const supportEmail = useSupportEmail(task.supportEmailId);
    const { content, fetchContent } = useSupportEmailContent(task.supportEmailId);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const openDialog = useCallback(() => {
        setIsDialogOpen(true);
        fetchContent();
    }, [fetchContent]);

    const closeDialog = useCallback(() => setIsDialogOpen(false), []);

    const closeTask = useCloseTask(task, goBack);

    if ( supportEmail === undefined ) return <LinearProgress />;

    return (
        <>
            <TitleHeader>Afklaring</TitleHeader>

            <ContentSection>
                <Container maxWidth="sm" disableGutters>
                    <Section>
                        {task.hasBeenPerformed ? <Message>Opgaven er allerede blevet udført</Message> : null}

                        <Card>
                            <CardHeader>{task.heading}</CardHeader>

                            <CardContent>
                                {supportEmail !== null && (
                                    <div className={classes.metadata}>Modtaget: {moment(supportEmail.email.date).format("D MMMM YYYY")}</div>
                                )}
                                {supportEmail === null && (
                                    <div className={classes.metadata}><em>Support-e-mailen findes ikke længere ({task.supportEmailId}).</em></div>
                                )}

                                <div className={classes.section}>
                                    <div className={classes.label}>Opsummering af email</div>
                                    <div>{task.summary}</div>
                                </div>

                                <div className={classes.section}>
                                    <div className={classes.label}>Behov for afklaring</div>
                                    <div>{task.requestForClarification}</div>
                                </div>
                            </CardContent>
                        </Card>

                        {!task.hasBeenPerformed ? (
                            <VerticalButtons>
                                <WhiteButton onClick={openDialog}>Vis e-mail</WhiteButton>
                                <GreenButton onClick={closeTask}>Afslut opgave</GreenButton>
                            </VerticalButtons>
                        ) : null}
                    </Section>
                </Container>
            </ContentSection>

            <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
                <DialogTitle>E-mail</DialogTitle>
                <DialogContent>
                    {content === undefined && <LinearProgress />}
                    {content === null && <p><em>E-mailen kunne ikke hentes.</em></p>}
                    {content && (
                        <>
                            <div className={classes.metadata}><strong>Fra:</strong> {content.from}</div>
                            <div className={classes.metadata}><strong>Til:</strong> {content.to}</div>
                            {content.cc && <div className={classes.metadata}><strong>Cc:</strong> {content.cc}</div>}
                            <div className={classes.metadata}><strong>Emne:</strong> {content.subject}</div>

                            {content.html
                                ? <iframe className={classes.htmlFrame} title="E-mail" sandbox="" srcDoc={content.html} />
                                : <div className={classes.body}>{content.text}</div>}

                            {content.attachments.length > 0 && (
                                <div className={classes.attachments}>
                                    <strong>Vedhæftninger:</strong> {content.attachments.map(attachment => attachment.filename).join(", ")}
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Luk</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
